import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { ChevronLeft, Plus, Trash2, ExternalLink, Loader2 } from 'lucide-react';
import InlineRenameInput from './InlineRenameInput';
import { SOURCE_TYPE_CONFIG } from './SourceTypeGrid';
import { cn } from '../lib/utils';
import api from '../lib/api';
import { toast } from 'sonner';

export default function SourceInstanceList({
  sourceType,
  instances,
  onBack,
  onAdd,
  onDelete,
  onRename,
  loading,
}) {
  const config = SOURCE_TYPE_CONFIG[sourceType] || {
    name: sourceType,
    icon: () => null,
    color: 'bg-neutral-100',
    textColor: 'text-neutral-900',
  };
  const Icon = config.icon;

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [instanceToDelete, setInstanceToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [affectedProjects, setAffectedProjects] = useState([]);

  const handleDeleteClick = (instance) => {
    setInstanceToDelete(instance);
    setAffectedProjects([]);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!instanceToDelete) return;
    setDeleting(true);
    try {
      const response = await api.delete(`/sources/${instanceToDelete.id}`);
      const affected = response.data.affected_projects || [];
      if (affected.length > 0) {
        toast.warning(
          `Deleted. ${affected.length} project(s) were using this source and may need reconfiguration.`,
          { duration: 6000 }
        );
      } else {
        toast.success('Source connection deleted');
      }
      setDeleteDialogOpen(false);
      setInstanceToDelete(null);
      if (onDelete) onDelete(instanceToDelete.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
      <Button
        variant="ghost"
        className="mb-4 pl-0 hover:bg-transparent hover:text-primary gap-1"
        onClick={onBack}
      >
        <ChevronLeft className="h-4 w-4" />
        Back to sources
      </Button>

      <Card>
        <div className="p-0">
          {/* Header */}
          <div className="p-6 border-b border-black/5 dark:border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div
                className={cn(
                  'h-12 w-12 rounded-2xl flex items-center justify-center',
                  config.color,
                  config.textColor,
                )}
              >
                <Icon className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-foreground">
                  {config.name}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {instances.length}{' '}
                  {instances.length === 1 ? 'source' : 'sources'} connected
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="gap-2 rounded-xl"
              onClick={onAdd}
            >
              <Plus className="h-3.5 w-3.5" />
              Add {config.name} Source
            </Button>
          </div>

          {/* Instance List */}
          {loading ? (
            <div className="divide-y divide-black/5 dark:divide-white/5">
              {[1, 2, 3].map((i) => (
                <div key={i} className="p-4 flex items-center gap-4 animate-pulse">
                  <div className="h-10 w-10 rounded-xl bg-muted" />
                  <div className="space-y-2 flex-1">
                    <div className="h-4 w-32 bg-muted rounded" />
                    <div className="h-3 w-48 bg-muted rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : instances.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-muted-foreground mb-4">
                No {config.name} sources connected yet
              </p>
              <Button onClick={onAdd}>
                <Plus className="h-4 w-4 mr-2" />
                Connect {config.name}
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-black/5 dark:divide-white/5">
              {instances.map((instance, index) => (
                <div
                  key={instance.id}
                  className="p-4 flex items-center justify-between hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition-colors animate-fade-up"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-xl bg-black/[0.03] dark:bg-white/[0.05] flex items-center justify-center text-muted-foreground">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div>
                      <InlineRenameInput
                        sourceId={instance.id}
                        value={instance.display_name || instance.source_type}
                        onRenamed={(newName) => {
                          if (onRename) onRename(instance.id, newName);
                        }}
                      />
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {instance.connection_id}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-500 hover:text-red-600 hover:bg-red-500/10 rounded-xl h-8 w-8"
                    onClick={() => handleDeleteClick(instance)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Source Connection?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{' '}
              <strong>{instanceToDelete?.display_name}</strong>?
              <br /><br />
              <span className="text-destructive font-medium">
                This may affect projects that depend on this connection.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
